import { ja } from "date-fns/locale";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextField from "../TextField";
import { useCallback, useMemo } from "react";
import { SpacingProps } from "@mui/system";
import { SxProps, Theme } from "@mui/material";

const toISO = {
  "datetime-local": (value: string) => `${value}:00`,
  date: (value: string) => `${value}T00:00:00`,
  month: (value: string) => `${value}-01T00:00:00`,
} as const;

const fromISO = {
  "datetime-local": (iso: string) => iso.split(".")[0].slice(0, -3),
  date: (iso: string) => iso.split("T")[0],
  month: (iso: string) => iso.split("T")[0].slice(0, -3),
} as const;

type PickerProps = {
  label: string;
  value: string;
  type: "datetime-local" | "date" | "month";
  minDate?: string;
  disabled?: boolean;
  error?: boolean;
  errorLabel?: string;
  sx?: SxProps<Theme>;
  forSearch?: boolean;
  replaceRangeErrorLabel?: boolean;
  parse?: (input: string) => string;
  decode?: (output: string) => string;
  onChange: (value?: string, inRange?: boolean) => unknown;
};

export const Picker = ({
  label,
  value,
  type,
  disabled,
  error,
  errorLabel: defaultErrorLabel,
  sx,
  forSearch,
  minDate,
  replaceRangeErrorLabel,
  parse,
  decode,
  onChange,
  ...rest
}: PickerProps & SpacingProps): JSX.Element => {
  const errorLabel = useMemo(() => {
    if (minDate == null || !replaceRangeErrorLabel || !value.length) {
      return defaultErrorLabel;
    }

    const minTime = new Date(toISO[type](minDate)).getTime();
    const selectedTime = new Date(
      toISO[type]((parse ?? fromISO[type])(value))
    ).getTime();

    return minTime <= selectedTime
      ? defaultErrorLabel
      : `${minDate.replace(/-/g, "/").replace("T", " ")} 以降の${
          type === "datetime-local" ? "日時" : type === "date" ? "日付" : "年月"
        }を選択してください。`;
  }, [defaultErrorLabel, minDate, parse, replaceRangeErrorLabel, type, value]);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (
        !replaceRangeErrorLabel ||
        minDate == null ||
        e.target.value.length === 0
      ) {
        onChange((decode ?? toISO[type])(e.target.value), true);
        return;
      }

      const newTime = new Date(toISO[type](e.target.value)).getTime();
      const minTime = new Date(toISO[type](minDate)).getTime();

      onChange((decode ?? toISO[type])(e.target.value), newTime >= minTime);
    },
    [replaceRangeErrorLabel, minDate, type, onChange, decode]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <TextField
        {...rest}
        label={label}
        value={(parse ?? fromISO[type])(value)}
        error={error}
        errorLabel={errorLabel}
        type={type}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{
          disableUnderline: forSearch,
          inputProps: {
            ...(minDate ? { min: minDate } : {}),
          },
        }}
        onChange={handleOnChange}
        disabled={disabled}
        minWidth={forSearch ? "100%" : undefined}
        sx={{
          ...sx,
          ...(forSearch
            ? {
                "& .MuiInputBase-input": {
                  border: error ? "1px solid #d32f2f" : "0.5px solid #c4c4c4",
                  borderRadius: "4px",
                  height: "30px",
                  padding: "4px",
                },
                "& .MuiFormLabel-root": {
                  zIndex: 100,
                  backgroundColor: "white",
                  mt: "6px",
                  pl: "10px",
                },
              }
            : {}),
        }}
      />
    </LocalizationProvider>
  );
};
